import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './session';

export type DrawingCategory = 'floor_plan' | 'structural_details' | 'working_drawing' | 'elevation';

export interface DrawingCategoryMeta {
  category: DrawingCategory;
  label: string;
  step: string;
  shortLabel: string;
  description: string;
  color: string;
  bg: string;
  borderColor: string;
}

export const DRAWING_CATEGORIES: Record<DrawingCategory, DrawingCategoryMeta> = {
  floor_plan: {
    category: 'floor_plan',
    label: 'Floor Plan',
    step: '1st',
    shortLabel: 'Floor Plan',
    description: '2D & 3D Floor Layouts, Architectural Floor Plans and 3D Views',
    color: '#2563eb',
    bg: '#eff6ff',
    borderColor: '#bfdbfe',
  },
  structural_details: {
    category: 'structural_details',
    label: 'Structural Details',
    step: '2nd',
    shortLabel: 'Structural Details',
    description: 'Column & Beam layouts, Foundation, Framing, and Structural Specifications',
    color: '#7c3aed',
    bg: '#f5f3ff',
    borderColor: '#ddd6fe',
  },
  working_drawing: {
    category: 'working_drawing',
    label: 'Working Drawing',
    step: '3rd',
    shortLabel: 'Working Drawing',
    description: 'Site Execution Drawings, Elevations, Sections, MEP and Plumbing Layouts',
    color: '#059669',
    bg: '#ecfdf5',
    borderColor: '#a7f3d0',
  },
  elevation: {
    category: 'elevation',
    label: '3D Elevation',
    step: '4th',
    shortLabel: '3D Elevation',
    description: '3D Perspective Renders, Exterior & Interior Visuals, and Elevation Files',
    color: '#0284c7',
    bg: '#f0f9ff',
    borderColor: '#bae6fd',
  },
};

export const CATEGORY_ORDER: DrawingCategory[] = [
  'floor_plan',
  'structural_details',
  'working_drawing',
  'elevation',
];

export const STANDARD_DRAWING_ORDER: DrawingCategory[] = [
  'floor_plan',
  'structural_details',
  'working_drawing',
  'elevation',
];

export interface ProjectDrawingRow {
  id: string;
  projectId: string;
  category: DrawingCategory;
  title: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl: string;
  version: number;
  isLatest: boolean;
  description: string | null;
  uploadedBy: string | null;
  uploadedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

export const drawingKeys = {
  all: ['project-drawings'] as const,
  byProject: (projectId: string) => ['project-drawings', projectId] as const,
  allLatest: ['project-drawings', 'all-latest'] as const,
};

/**
 * Fetch all drawings for a single project (both active latest and revision history).
 */
export function useProjectDrawings(projectId: string) {
  return useQuery({
    queryKey: drawingKeys.byProject(projectId),
    enabled: !!projectId,
    queryFn: async () => {
      const rows = unwrap(
        await supabase
          .from('project_drawings')
          .select('*')
          .eq('project_id', projectId)
          .is('deleted_at', null)
          .order('version', { ascending: false })
          .order('created_at', { ascending: false }),
      );

      const all: ProjectDrawingRow[] = (rows ?? []).map((r) => ({
        id: r.id,
        projectId: r.project_id,
        category: r.category as DrawingCategory,
        title: r.title,
        fileName: r.file_name,
        fileType: r.file_type,
        fileSize: Number(r.file_size || 0),
        fileUrl: r.file_url,
        version: r.version,
        isLatest: r.is_latest,
        description: r.description,
        uploadedBy: r.uploaded_by,
        uploadedByName: r.uploaded_by_name,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));

      const latest: Record<DrawingCategory, ProjectDrawingRow | null> = {
        floor_plan: null,
        structural_details: null,
        working_drawing: null,
        elevation: null,
      };

      const historyByCategory: Record<DrawingCategory, ProjectDrawingRow[]> = {
        floor_plan: [],
        structural_details: [],
        working_drawing: [],
        elevation: [],
      };

      const elevations: ProjectDrawingRow[] = [];

      for (const d of all) {
        if (d.category === 'elevation') {
          elevations.push(d);
          if (!latest.elevation) {
            latest.elevation = d;
          }
        } else {
          if (!latest[d.category] && d.isLatest) {
            latest[d.category] = d;
          } else if (!latest[d.category]) {
            latest[d.category] = d;
          }
        }
        historyByCategory[d.category].push(d);
      }

      return {
        all,
        latest,
        elevations,
        historyByCategory,
        totalCount: all.length,
      };
    },
  });
}

export interface ProjectDrawingsMapEntry {
  floor_plan: ProjectDrawingRow | null;
  structural_details: ProjectDrawingRow | null;
  working_drawing: ProjectDrawingRow | null;
  elevation: ProjectDrawingRow | null;
  elevationCount: number;
  elevations: ProjectDrawingRow[];
}

/**
 * Fetch latest drawings for all projects for the Projects List table.
 */
export function useAllProjectsLatestDrawings() {
  return useQuery({
    queryKey: drawingKeys.allLatest,
    queryFn: async () => {
      const rows = unwrap(
        await supabase
          .from('project_drawings')
          .select('*')
          .eq('is_latest', true)
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
      );

      const map = new Map<string, ProjectDrawingsMapEntry>();

      for (const r of rows ?? []) {
        const row: ProjectDrawingRow = {
          id: r.id,
          projectId: r.project_id,
          category: r.category as DrawingCategory,
          title: r.title,
          fileName: r.file_name,
          fileType: r.file_type,
          fileSize: Number(r.file_size || 0),
          fileUrl: r.file_url,
          version: r.version,
          isLatest: r.is_latest,
          description: r.description,
          uploadedBy: r.uploaded_by,
          uploadedByName: r.uploaded_by_name,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        };

        if (!map.has(row.projectId)) {
          map.set(row.projectId, {
            floor_plan: null,
            structural_details: null,
            working_drawing: null,
            elevation: null,
            elevationCount: 0,
            elevations: [],
          });
        }

        const entry = map.get(row.projectId)!;
        if (row.category === 'elevation') {
          entry.elevations.push(row);
          entry.elevationCount += 1;
          if (!entry.elevation) {
            entry.elevation = row;
          }
        } else {
          entry[row.category] = row;
        }
      }

      return map;
    },
  });
}

export interface NewDrawingPayload {
  projectId: string;
  category: DrawingCategory;
  title: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl: string;
  description?: string;
}

/**
 * Upload / save a new project drawing revision.
 */
export function useUploadDrawing(projectId?: string) {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (payload: NewDrawingPayload) => {
      const companyId = actor?.companyId;
      if (!companyId) throw new Error('Not signed in');

      const { data, error } = await supabase
        .from('project_drawings')
        .insert({
          company_id: companyId,
          project_id: payload.projectId,
          category: payload.category,
          title: payload.title.trim() || payload.fileName,
          file_name: payload.fileName,
          file_type: payload.fileType,
          file_size: payload.fileSize,
          file_url: payload.fileUrl,
          description: payload.description?.trim() || null,
          uploaded_by: actor.id,
          uploaded_by_name: actor.fullName || actor.email,
        })
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_, vars) => {
      const pId = vars.projectId || projectId;
      if (pId) {
        void qc.invalidateQueries({ queryKey: drawingKeys.byProject(pId) });
        void qc.invalidateQueries({ queryKey: ['project', pId] });
      }
      void qc.invalidateQueries({ queryKey: drawingKeys.allLatest });
      void qc.invalidateQueries({ queryKey: drawingKeys.all });
    },
  });
}

/**
 * Batch upload multiple project drawing files across categories (floor_plan, structural_details, working_drawing).
 */
export function useUploadMultipleDrawings(projectId?: string) {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (payloads: NewDrawingPayload[]) => {
      const companyId = actor?.companyId;
      if (!companyId) throw new Error('Not signed in');
      if (!payloads.length) return [];

      const inserts = payloads.map((payload) => ({
        company_id: companyId,
        project_id: payload.projectId,
        category: payload.category,
        title: payload.title.trim() || payload.fileName,
        file_name: payload.fileName,
        file_type: payload.fileType,
        file_size: payload.fileSize,
        file_url: payload.fileUrl,
        description: payload.description?.trim() || null,
        uploaded_by: actor.id,
        uploaded_by_name: actor.fullName || actor.email,
      }));

      const { data, error } = await supabase
        .from('project_drawings')
        .insert(inserts)
        .select();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_, vars) => {
      const pId = vars[0]?.projectId || projectId;
      if (pId) {
        void qc.invalidateQueries({ queryKey: drawingKeys.byProject(pId) });
        void qc.invalidateQueries({ queryKey: ['project', pId] });
      }
      void qc.invalidateQueries({ queryKey: drawingKeys.allLatest });
      void qc.invalidateQueries({ queryKey: drawingKeys.all });
    },
  });
}

/**
 * Batch upload multiple 3D elevation files / images.
 */
export function useUploadMultipleElevations(projectId?: string) {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (payloads: NewDrawingPayload[]) => {
      const companyId = actor?.companyId;
      if (!companyId) throw new Error('Not signed in');
      if (!payloads.length) return [];

      const inserts = payloads.map((payload) => ({
        company_id: companyId,
        project_id: payload.projectId,
        category: 'elevation' as const,
        title: payload.title.trim() || payload.fileName,
        file_name: payload.fileName,
        file_type: payload.fileType,
        file_size: payload.fileSize,
        file_url: payload.fileUrl,
        description: payload.description?.trim() || null,
        uploaded_by: actor.id,
        uploaded_by_name: actor.fullName || actor.email,
      }));

      const { data, error } = await supabase
        .from('project_drawings')
        .insert(inserts)
        .select();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_, vars) => {
      const pId = vars[0]?.projectId || projectId;
      if (pId) {
        void qc.invalidateQueries({ queryKey: drawingKeys.byProject(pId) });
        void qc.invalidateQueries({ queryKey: ['project', pId] });
      }
      void qc.invalidateQueries({ queryKey: drawingKeys.allLatest });
      void qc.invalidateQueries({ queryKey: drawingKeys.all });
    },
  });
}

/**
 * Delete a drawing (Admin only soft delete).
 * When the latest version is deleted, the immediate prior version becomes active.
 */
export function useDeleteDrawing(projectId?: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      category,
      targetProjectId,
    }: {
      id: string;
      category: DrawingCategory;
      targetProjectId: string;
    }) => {
      // 1. Try RPC function first
      try {
        const { error: rpcError } = await (supabase.rpc as any)('delete_project_drawing', {
          p_drawing_id: id,
        });
        if (!rpcError) {
          return { id, targetProjectId };
        }
      } catch {
        // Fall back to direct queries below
      }

      // 2. Direct soft delete
      const { error: delError } = await supabase
        .from('project_drawings')
        .update({ deleted_at: new Date().toISOString(), is_latest: false })
        .eq('id', id);

      if (delError) {
        // 3. Fallback: if soft-delete update was blocked by RLS, attempt direct delete
        const { error: hardDelError } = await supabase
          .from('project_drawings')
          .delete()
          .eq('id', id);

        if (hardDelError) {
          throw new Error(delError.message || hardDelError.message);
        }
      }

      // Check if there are other remaining non-deleted versions for this category
      const { data: remaining } = await supabase
        .from('project_drawings')
        .select('id')
        .eq('project_id', targetProjectId)
        .eq('category', category)
        .is('deleted_at', null)
        .order('version', { ascending: false })
        .limit(1);

      if (remaining && remaining.length > 0 && remaining[0]) {
        // Promote the newest remaining version to is_latest = true
        await supabase
          .from('project_drawings')
          .update({ is_latest: true })
          .eq('id', remaining[0].id);
      }

      return { id, targetProjectId };
    },
    onSuccess: (result) => {
      const pId = result.targetProjectId || projectId;
      if (pId) {
        void qc.invalidateQueries({ queryKey: drawingKeys.byProject(pId) });
        void qc.invalidateQueries({ queryKey: ['project', pId] });
      }
      void qc.invalidateQueries({ queryKey: drawingKeys.allLatest });
      void qc.invalidateQueries({ queryKey: drawingKeys.all });
    },
  });
}

/**
 * Restore a soft-deleted drawing.
 */
export function useRestoreDrawing(projectId?: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, category, targetProjectId }: { id: string; category: DrawingCategory; targetProjectId: string }) => {
      // Restore drawing
      const { error } = await supabase
        .from('project_drawings')
        .update({ deleted_at: null })
        .eq('id', id);

      if (error) throw new Error(error.message);

      // Re-evaluate which version is highest
      const { data: allActive } = await supabase
        .from('project_drawings')
        .select('id, version')
        .eq('project_id', targetProjectId)
        .eq('category', category)
        .is('deleted_at', null)
        .order('version', { ascending: false });

      if (allActive && allActive.length > 0 && allActive[0]) {
        const topId = allActive[0].id;
        for (const item of allActive) {
          await supabase
            .from('project_drawings')
            .update({ is_latest: item.id === topId })
            .eq('id', item.id);
        }
      }

      return { id, targetProjectId };
    },
    onSuccess: (result) => {
      const pId = result.targetProjectId || projectId;
      if (pId) {
        void qc.invalidateQueries({ queryKey: drawingKeys.byProject(pId) });
      }
      void qc.invalidateQueries({ queryKey: drawingKeys.allLatest });
      void qc.invalidateQueries({ queryKey: drawingKeys.all });
    },
  });
}

/**
 * Format bytes to readable size (e.g. 1.8 MB).
 */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Converts a data: URL to a Blob Object URL so modern browsers (especially Chromium)
 * do not block top-frame navigation with `about:blank` or block file downloads.
 */
export function convertToBlobUrl(fileUrl: string, fileType?: string, fileName?: string): string {
  if (!fileUrl || !fileUrl.startsWith('data:')) {
    return fileUrl;
  }
  try {
    const commaIdx = fileUrl.indexOf(',');
    if (commaIdx === -1) return fileUrl;

    const header = fileUrl.slice(0, commaIdx);
    const data = fileUrl.slice(commaIdx + 1);

    const mimeMatch = header.match(/data:([^;]+)/);
    const mime =
      mimeMatch?.[1] ||
      fileType ||
      (/\.pdf$/i.test(fileName || '') ? 'application/pdf' : 'application/octet-stream');

    const isBase64 = header.includes(';base64');
    let bytes: Uint8Array;

    if (isBase64) {
      const binaryString = atob(data);
      const len = binaryString.length;
      bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
    } else {
      const decoded = decodeURIComponent(data);
      bytes = new TextEncoder().encode(decoded);
    }

    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error('Error converting data URL to blob URL:', err);
    return fileUrl;
  }
}

/**
 * Safely triggers browser download for a drawing or document.
 */
export function triggerFileDownload(fileUrl: string, fileName: string, fileType?: string) {
  if (!fileUrl) return;

  let url = fileUrl;
  let isCreatedBlob = false;
  if (url.startsWith('data:')) {
    const blobUrl = convertToBlobUrl(url, fileType, fileName);
    if (blobUrl !== url) {
      url = blobUrl;
      isCreatedBlob = true;
    }
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (isCreatedBlob) {
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }
}

/**
 * Safely opens a drawing or PDF in a new browser tab without getting blocked as about:blank.
 */
export function openDrawingInNewTab(fileUrl: string, fileName?: string, fileType?: string) {
  if (!fileUrl) return;

  let targetUrl = fileUrl;
  if (targetUrl.startsWith('data:')) {
    targetUrl = convertToBlobUrl(targetUrl, fileType, fileName);
  }

  const a = document.createElement('a');
  a.href = targetUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
